const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

let refreshPromise: Promise<boolean> | null = null;

/**
 * Error thrown by `apiFetch` for non-2xx responses. Callers can branch on
 * `status` to render restricted-access copy on 403, retry on 5xx, etc.
 * The `code` and `body` fields surface the API's error envelope when present.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('ida_refresh_token');
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const result = data.data ?? data;
    if (result.accessToken) {
      localStorage.setItem('ida_access_token', result.accessToken);
      if (result.refreshToken) {
        localStorage.setItem('ida_refresh_token', result.refreshToken);
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const doFetch = async () => {
    const token = localStorage.getItem('ida_access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      const apiKey = import.meta.env.VITE_API_KEY;
      if (apiKey) {
        headers['X-API-Key'] = apiKey;
      }
    }

    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    return fetch(`${API_BASE}${path}`, { ...options, headers });
  };

  let res = await doFetch();

  // On 401, try refreshing the token once and retry
  if (res.status === 401 && localStorage.getItem('ida_refresh_token')) {
    // Deduplicate concurrent refresh attempts
    if (!refreshPromise) {
      refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
    }
    const refreshed = await refreshPromise;
    if (refreshed) {
      res = await doFetch();
    }
  }

  if (res.status === 401) {
    // Clear stale tokens so the auth:unauthorized handler and ProtectedRoute
    // can tell that the session is genuinely gone (not just a transient 401).
    localStorage.removeItem('ida_access_token');
    localStorage.removeItem('ida_refresh_token');
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new ApiError('Unauthorized', 401);
  }

  if (!res.ok) {
    let body: unknown;
    let message = `API error: ${res.status}`;
    let code: string | undefined;
    try {
      body = await res.json();
      // API envelope: { success: false, error: { code, message } }  OR  { error: "string", message: "string" }
      const env = body as { error?: string | { code?: string; message?: string }; message?: string; code?: string };
      if (env?.error) {
        if (typeof env.error === 'string') {
          message = env.error;
        } else {
          message = env.error.message ?? env.error.code ?? message;
          code = env.error.code;
        }
      } else if (env?.message) {
        message = env.message;
      }
      if (!code) code = env?.code;
    } catch {
      // body wasn't JSON — keep the status-based default message
    }
    throw new ApiError(message, res.status, code, body);
  }
  const data = await res.json();
  return data.data ?? data;
}
